import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Product, ProductFormData } from '../types';
import { productService } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';
import ProductForm from '../components/ProductForm';
import './Admin.css';

const Admin: React.FC = () => {
  const { isAuthenticated, login, logout } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLogin, setShowLogin] = useState(false);
  const [adminKey, setAdminKey] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [deletingProduct, setDeletingProduct] = useState<number | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      fetchProducts();
    }
  }, [isAuthenticated]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const data = await productService.getAllProducts();
      setProducts(data);
      setError(null);
    } catch (err) {
      setError('Failed to load products');
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await login(adminKey);
    if (success) {
      setShowLogin(false);
      setAdminKey('');
    } else {
      setError('Invalid admin key');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await productService.deleteProduct(id);
      setProducts(products.filter(p => p.id !== id));
      setDeletingProduct(null);
    } catch (err) {
      setError('Failed to delete product');
      console.error('Error deleting product:', err);
    }
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setShowForm(true);
  };

  const handleAdd = () => {
    setEditingProduct(null);
    setShowForm(true);
  };

  const handleFormSubmit = async (formData: ProductFormData) => {
    try {
      if (editingProduct) {
        await productService.updateProduct(editingProduct.id, formData);
        setProducts(products.map(p => p.id === editingProduct.id ? { ...p, ...formData } : p));
      } else {
        await productService.addProduct(formData);
        fetchProducts(); // Refresh the list
      }
      setShowForm(false);
      setEditingProduct(null);
    } catch (err) {
      setError('Failed to save product');
      console.error('Error saving product:', err);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingProduct(null);
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-login">
        <div className="login-container">
          <h2>Admin Login</h2>
          <form onSubmit={handleLogin} className="login-form">
            <div className="form-group">
              <label htmlFor="adminKey">Admin Key:</label>
              <input
                type="password"
                id="adminKey"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                required
                className="form-input"
              />
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => window.history.back()} className="cancel-button">
                Cancel
              </button>
              <button type="submit" className="login-button">
                Login
              </button>
            </div>
          </form>
          {error && <p className="error-message">{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="admin">
      <div className="admin-header">
        <h1>Product Management</h1>
        <button onClick={logout} className="logout-button">
          Logout
        </button>
      </div>

      {error && (
        <div className="error-banner">
          <p>{error}</p>
          <button onClick={() => setError(null)} className="close-error">×</button>
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading products...</p>
        </div>
      ) : (
        <>
          <div className="products-table-container">
            <table className="products-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Dimensions</th>
                  <th>Price</th>
                  <th>Material</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id}>
                    <td>
                      <img 
                        src={getImageUrl(product.img_path)} 
                        alt={product.name}
                        className="product-thumbnail"
                        onLoad={() => {
                          console.log(`Admin thumbnail loaded: ${product.name} - ${product.img_path}`);
                        }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          console.error(`Failed to load admin thumbnail for product ${product.name}:`, {
                            productId: product.id,
                            imgPath: product.img_path,
                            fullUrl: getImageUrl(product.img_path),
                            error: e
                          });
                          target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
                        }}
                      />
                    </td>
                    <td>{product.name}</td>
                    <td>{product.category}</td>
                    <td>{product.length}×{product.height}×{product.depth} cm</td>
                    <td>€{product.price.toFixed(2)}</td>
                    <td>{product.material}</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          onClick={() => handleEdit(product)}
                          className="edit-button"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => setDeletingProduct(product.id)}
                          className="delete-button"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="admin-actions">
            <button onClick={handleAdd} className="add-button">
              Add New Product
            </button>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {deletingProduct && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this product? This action cannot be undone.</p>
            <div className="modal-actions">
              <button 
                onClick={() => setDeletingProduct(null)}
                className="cancel-button"
              >
                Cancel
              </button>
              <button 
                onClick={() => handleDelete(deletingProduct)}
                className="confirm-delete-button"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product Form Modal */}
      {showForm && (
        <ProductForm
          product={editingProduct}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      )}
    </div>
  );
};

export default Admin;
